/**
 * Slovak Translations — Impact Core i18n
 *
 * Flat key-value structure. Keys use dot notation for namespacing.
 * Coverage: navigation, actions, statuses, dashboard labels, form labels.
 */

export const sk: Record<string, string> = {
  // Navigation
  'nav.dashboard': 'Prehľad',
  'nav.funds': 'Fondy',
  'nav.investments': 'Investície',
  'nav.investees': 'Príjemcovia investícií',
  'nav.esg': 'ESG Preskúmanie',
  'nav.compliance': 'Súlad',
  'nav.reports': 'Správy',
  'nav.settings': 'Nastavenia',
  'nav.users': 'Používatelia',
  'nav.sfdr': 'SFDR',
  'nav.pai': 'PAI Ukazovatele',
  'nav.taxonomy': 'Taxonómia EÚ',
  'nav.filings': 'Podania',
  'nav.calendar': 'Kalendár',
  'nav.submit': 'Odoslať údaje',
  'nav.submissions': 'Moje podania',
  'nav.benchmarks': 'Referenčné hodnoty',
  'nav.messages': 'Správy',
  'nav.portfolio': 'Portfólio',
  'nav.performance': 'Výkonnosť fondov',
  'nav.esgSummary': 'ESG Súhrn',
  'nav.pipeline': 'Pipeline',
  'nav.dueDiligence': 'Hĺbková kontrola',
  'nav.impact': 'Ukazovatele dopadu',
  'nav.sdg': 'Mapovanie CUR',
  'nav.audit': 'Auditná stopa',
  'nav.snapshots': 'Dátové snímky',
  'nav.knowledgeGraph': 'Graf znalostí',
  'nav.geographic': 'Geografický',
  'nav.bi': 'BI Štúdio',
  'nav.ai': 'AI Asistent',
  'nav.tasks': 'Úlohy',
  'nav.csrd': 'CSRD',
  'nav.regulatory': 'Regulačný monitor',

  // Common Actions
  'action.create': 'Vytvoriť',
  'action.edit': 'Upraviť',
  'action.delete': 'Odstrániť',
  'action.save': 'Uložiť',
  'action.cancel': 'Zrušiť',
  'action.submit': 'Odoslať',
  'action.approve': 'Schváliť',
  'action.reject': 'Zamietnuť',
  'action.download': 'Stiahnuť',
  'action.export': 'Exportovať',
  'action.import': 'Importovať',
  'action.filter': 'Filtrovať',
  'action.search': 'Hľadať',
  'action.refresh': 'Obnoviť',
  'action.close': 'Zavrieť',
  'action.back': 'Späť',
  'action.next': 'Ďalej',
  'action.previous': 'Predchádzajúci',
  'action.viewAll': 'Zobraziť všetko',
  'action.viewDetails': 'Zobraziť podrobnosti',
  'action.generate': 'Generovať',
  'action.signOut': 'Odhlásiť sa',
  'action.addNew': 'Pridať nové',
  'action.invite': 'Pozvať',

  // Statuses
  'status.draft': 'Návrh',
  'status.submitted': 'Odoslané',
  'status.validated': 'Validované',
  'status.approved': 'Schválené',
  'status.rejected': 'Zamietnuté',
  'status.pending': 'Čakajúce',
  'status.inProgress': 'Prebieha',
  'status.overdue': 'Po termíne',
  'status.compliant': 'V súlade',
  'status.filed': 'Podané',
  'status.active': 'Aktívne',
  'status.inactive': 'Neaktívne',
  'status.closed': 'Uzavreté',
  'status.fundraising': 'Získavanie prostriedkov',
  'status.investing': 'Investovanie',
  'status.harvesting': 'Zber',
  'status.validating': 'Validácia',
  'status.review': 'V preskúmaní',
  'status.monitoring': 'Monitorovanie',
  'status.exiting': 'Výstup',
  'status.pipeline': 'Pipeline',
  'status.due_diligence': 'Hĺbková kontrola',
  'status.disbursed': 'Vyplatené',
  'status.restructured': 'Reštrukturalizované',
  'status.matured': 'Splatné',
  'status.written_off': 'Odpísané',
  'status.exited': 'Vystúpil',
  'status.incomplete': 'Neúplné',
  'status.open': 'Otvorené',
  'status.in_progress': 'Prebieha',
  'status.completed': 'Dokončené',
  'status.cancelled': 'Zrušené',
  'status.accepted': 'Prijaté',
  'status.failed': 'Zlyhalo',

  // Dashboard Labels
  'dashboard.portfolioOverview': 'Prehľad portfólia',
  'dashboard.complianceStatus': 'Stav súladu',
  'dashboard.collectionProgress': 'Priebeh zberu',
  'dashboard.activityFeed': 'Aktivita',
  'dashboard.quickActions': 'Rýchle akcie',
  'dashboard.investmentPipeline': 'Investičný pipeline',
  'dashboard.totalFunds': 'Celkom fondov',
  'dashboard.aum': 'AUM',
  'dashboard.activeInvestments': 'Aktívne investície',
  'dashboard.totalInvestees': 'Príjemcovia investícií',
  'dashboard.recentActivity': 'Nedávna aktivita',

  // LP Dashboard Labels
  'lp.totalCommitted': 'Celkom záväzkov',
  'lp.capitalCalled': 'Vyžiadaný kapitál',
  'lp.distributed': 'Distribuované',
  'lp.netPosition': 'Čistá pozícia',
  'lp.tvpi': 'TVPI',
  'lp.dpi': 'DPI',
  'lp.fundPerformance': 'Výkonnosť fondov',
  'lp.esgSummary': 'ESG Súhrn',
  'lp.reportLibrary': 'Knižnica správ',
  'lp.taxonomyAligned': 'Súlad s taxonómiou',
  'lp.paiCoverage': 'PAI Pokrytie',
  'lp.portfolioPerformance': 'Výkonnosť LP portfólia',

  // Fund Labels
  'fund.name': 'Názov fondu',
  'fund.code': 'Kód fondu',
  'fund.vintage': 'Rok vzniku',
  'fund.size': 'Veľkosť fondu',
  'fund.targetSize': 'Cieľová veľkosť',
  'fund.currency': 'Mena',
  'fund.strategy': 'Stratégia',
  'fund.status': 'Stav',
  'fund.sfdrCategory': 'SFDR Kategória',
  'fund.geographyFocus': 'Geografické zameranie',
  'fund.sectorFocus': 'Sektorové zameranie',

  // Form Labels
  'form.name': 'Názov',
  'form.email': 'E-mail',
  'form.country': 'Krajina',
  'form.amount': 'Čiastka',
  'form.currency': 'Mena',
  'form.date': 'Dátum',
  'form.description': 'Popis',
  'form.notes': 'Poznámky',
  'form.type': 'Typ',
  'form.period': 'Obdobie',
  'form.year': 'Rok',
  'form.required': 'Povinné',
  'form.optional': 'Nepovinné',

  // Table Headers
  'table.fund': 'Fond',
  'table.commitment': 'Záväzok',
  'table.called': 'Vyžiadané',
  'table.distributed': 'Distribuované',
  'table.tvpi': 'TVPI',
  'table.status': 'Stav',
  'table.regulation': 'Regulácia',
  'table.filingType': 'Typ podania',
  'table.reportingPeriod': 'Obdobie',
  'table.createdAt': 'Vytvorené',
  'table.actions': 'Akcie',

  // Compliance
  'compliance.sfdr': 'SFDR',
  'compliance.taxonomy': 'Taxonómia EÚ',
  'compliance.pai': 'PAI',
  'compliance.dataCompleteness': 'Úplnosť údajov',
  'compliance.nextDeadline': 'Nasledujúci termín',

  // Reports
  'report.quarterly': 'Štvrťročná správa',
  'report.annual': 'Výročná správa',
  'report.paiStatement': 'PAI Vyhlásenie',
  'report.taxonomyReport': 'Správa o taxonómii',
  'report.generatedAt': 'Vygenerované',
  'report.version': 'Verzia',

  // General
  'general.loading': 'Načítavanie...',
  'general.noData': 'Žiadne dostupné údaje',
  'general.error': 'Vyskytla sa chyba',
  'general.retry': 'Skúsiť znova',
  'general.total': 'Celkom',
  'general.page': 'Stránka',
  'general.of': 'z',
  'general.showing': 'Zobrazenie',
  'general.results': 'výsledkov',
  'general.language': 'Jazyk',
  'general.english': 'Angličtina',
  'general.french': 'Francúzština',

  // Tour
  'tour.takeTour': 'Spustiť prehliadku',
  'tour.skip': 'Preskočiť',
  'tour.next': 'Ďalší',
  'tour.prev': 'Predchádzajúci',
  'tour.finish': 'Dokončiť',


  // Tour Content, Form Labels, Table Headers, Actions, Errors (i18n wiring)
  'tour.sidebarTitle': 'Navigácia',
  'tour.sidebarContent': 'Použite bočný panel na navigáciu medzi správou portfólia, zberom údajov ESG, dodržiavaním predpisov, reportmi a analytickými nástrojmi.',
  'tour.kpisTitle': 'Prehľad portfólia',
  'tour.kpisContent': 'Tieto karty zobrazujú vaše portfólio na prvý pohľad — celkové fondy, AUM, aktívne investície a počet subjektov.',
  'tour.actionsTitle': 'Rýchle akcie',
  'tour.actionsContent': 'Vytvárajte fondy, pridávajte investície, pozývajte subjekty a generujte reporty jedným kliknutím.',
  'tour.languageTitle': 'Jazyk',
  'tour.languageContent': 'Prepínajte medzi 24 oficiálnymi jazykmi EÚ. Celé rozhranie sa preloží okamžite.',
  'tour.aiTitle': 'AI Asistent',
  'tour.aiContent': 'Pýtajte sa na svoje portfólio kedykoľvek. AI asistent má plný kontext o vašich fondoch a investíciách.',
  'action.createFund': 'Vytvoriť fond',
  'action.editFund': 'Upraviť fond',
  'action.addInvestment': 'Pridať investíciu',
  'action.addInvestee': 'Pridať subjekt',
  'action.editInvestee': 'Upraviť subjekt',
  'action.createTemplate': 'Vytvoriť šablónu',
  'action.createSubmission': 'Vytvoriť podanie',
  'action.createFiling': 'Vytvoriť podanie',
  'action.generateReport': 'Generovať report',
  'action.inviteUser': 'Pozvať používateľa',
  'action.sendInvitation': 'Odoslať pozvánku',
  'action.saveChanges': 'Uložiť zmeny',
  'form.fundName': 'Názov fondu',
  'form.fundCode': 'Kód fondu',
  'form.sfdrCategory': 'SFDR Kategória',
  'form.baseCurrency': 'Základná mena',
  'form.targetSize': 'Cieľová veľkosť',
  'form.vintageYear': 'Rok založenia',
  'form.geographyFocus': 'Geografické zameranie',
  'form.sectorFocus': 'Sektorové zameranie',
  'form.investmentCode': 'Kód investície',
  'form.instrumentType': 'Typ nástroja',
  'form.maturityDate': 'Dátum splatnosti',
  'form.sector': 'Sektor',
  'form.countryCode': 'Kód krajiny',
  'form.entityType': 'Typ subjektu',
  'form.region': 'Región',
  'form.staffCount': 'Počet zamestnancov',
  'form.borrowers': 'Dlžníci',
  'form.contactName': 'Meno kontaktu',
  'form.contactEmail': 'Kontaktný e-mail',
  'form.templateName': 'Názov šablóny',
  'form.templateType': 'Typ šablóny',
  'form.indicators': 'Ukazovatele',
  'form.reportingPeriod': 'Reportovacie obdobie',
  'form.reportingYear': 'Rok reportu',
  'form.regulation': 'Regulácia',
  'form.filingType': 'Typ podania',
  'form.reportType': 'Typ reportu',
  'form.emailAddress': 'E-mailová adresa',
  'form.role': 'Rola',
  'form.selectFund': 'Vyberte fond...',
  'form.selectInvestee': 'Vyberte subjekt...',
  'table.code': 'Kód',
  'table.investee': 'Subjekt',
  'table.instrument': 'Nástroj',
  'table.amount': 'Čiastka',
  'table.currency': 'Mena',
  'table.maturity': 'Splatnosť',
  'table.name': 'Názov',
  'table.country': 'Krajina',
  'table.type': 'Typ',
  'table.period': 'Obdobie',
  'table.completion': 'Dokončenie',
  'table.submitted': 'Odoslané',
  'table.version': 'Verzia',
  'table.indicators': 'Ukazovatele',
  'table.dueDate': 'Termín',
  'table.generated': 'Vygenerované',
  'table.email': 'E-mail',
  'table.role': 'Rola',
  'error.createFailed': 'Vytvorenie zlyhalo. Skúste to znova.',
  'error.updateFailed': 'Aktualizácia zlyhala. Skúste to znova.',
  'error.loadFailed': 'Načítanie dát zlyhalo.',

  // Sidebar Sections
  'sections.portfolio': 'PORTFÓLIO',
  'sections.dataCollection': 'ZBER DÁT',
  'sections.intelligence': 'ANALYTIKA',
  'sections.ecosystem': 'EKOSYSTÉM',
  'sections.admin': 'SPRÁVA',
  'sections.other': 'OSTATNÉ',

  // Page Titles
  'pages.messagesTitle': 'Správy',
  'pages.taxonomyTitle': 'Taxonómia EÚ',
  'pages.biStudioTitle': 'BI Studio',
  'pages.knowledgeGraphTitle': 'Graf znalostí',
  'pages.benchmarksTitle': 'Benchmarky',
  'pages.mcpTitle': 'Integrácia MCP',
  'pages.tasksTitle': 'Úlohy',
  'pages.submitDataTitle': 'Odoslať ESG údaje',

  // Empty States
  'empty.benchmarksComingSoon': 'Benchmarky už čoskoro',

  // Labels
  'labels.sfdr': 'SFDR',
  'labels.euTaxonomy': 'Taxonómia EÚ',
  'labels.csrd': 'CSRD',
  'labels.mifid': 'MiFID',
  'labels.other': 'Ostatné',
  'labels.critical': 'Kritický',
  'labels.high': 'Vysoký',
  'labels.medium': 'Stredný',
  'labels.low': 'Nízky',

  // Count / summary strings
  'count.fundsTotal': '{count} fund(s) total',
  'count.investmentsTotal': '{count} investment(s) total',
  'count.investeesTotal': '{count} investee(s) total',

  // Filter options
  'filter.allStatus': 'All Status',
  'filter.allSfdr': 'All SFDR',

  // Empty states
  'empty.noFundsYet': 'No funds yet. Create your first fund.',
  'empty.noFundsMatch': 'No funds match your filters.',
  'empty.noInvestmentsYet': 'No investments yet.',
  'empty.noInvestmentsMatch': 'No investments match your search.',
  'empty.noInvesteesYet': 'No investees yet.',
  'empty.noInvesteesMatch': 'No investees match your search.',
  'empty.noTemplatesYet': 'No ESG templates configured yet.',
  'empty.noSubmissionsYet': 'No ESG submissions yet.',
  'empty.noComplianceData': 'No SFDR compliance data available.',
  'empty.noFilingsYet': 'No compliance filings yet.',
  'empty.noReportsYet': 'No reports generated yet.',
  'empty.selectFundPai': 'Select a fund to view PAI indicators.',
  'empty.noPaiIndicators': 'No PAI indicators found for this fund.',
  'empty.selectFundCalendar': 'Select a fund to view compliance calendar.',
  'empty.noCalendarEvents': 'No upcoming events for this fund.',

  // Page subtitles
  'subtitle.esg': 'Templates and data submissions',
  'subtitle.compliance': 'SFDR, PAI indicators, and regulatory filings',
  'subtitle.reports': 'Generate and manage compliance reports',
  'subtitle.settings': 'Manage your account and preferences',

  // Tab labels
  'tab.templates': 'Templates',
  'tab.submissions': 'Submissions',
  'tab.sfdr': 'SFDR',
  'tab.paiIndicators': 'PAI Indicators',
  'tab.filings': 'Filings',
  'tab.reports': 'Reports',
  'tab.calendar': 'Calendar',

  // Compliance table headers
  'table.indicator': 'Indicator',
  'table.category': 'Category',
  'table.metric': 'Metric',
  'table.value': 'Value',
  'table.mandatory': 'Mandatory',
  'table.data': 'Data',
  'table.deadline': 'Deadline',
  'table.taxonomy': 'Taxonomy',

  // Yes/No
  'general.yes': 'Yes',
  'general.no': 'No',

  // Error messages
  'error.loadTemplates': 'Failed to load templates.',
  'error.loadSubmissions': 'Failed to load submissions.',
  'error.loadCompliance': 'Failed to load compliance data.',
  'error.loadPai': 'Failed to load PAI indicators.',
  'error.loadFilings': 'Failed to load filings.',
  'error.loadCalendar': 'Failed to load calendar events.',
  'error.createTemplate': 'Failed to create template. Please try again.',
  'error.createSubmission': 'Failed to create submission. Please try again.',
  'error.createFiling': 'Failed to create filing. Please try again.',
  'error.generateReport': 'Failed to generate report.',
  'error.templateNameRequired': 'Template name is required',
  'error.selectFund': 'Select a fund',
  'error.selectInvestee': 'Select an investee',
  'error.validAmount': 'Enter a valid amount',
  'error.reportingPeriodRequired': 'Reporting period is required',
  'error.validYearRequired': 'Valid year is required',
  'error.enterPeriod': 'Enter a period',
  'error.nameRequired': 'Name is required',
  'error.countryCodeRequired': 'Country code is required',
  'error.operationFailed': 'Operation failed.',
  'error.pleaseselectFund': 'Please select a fund',

  // Success messages
  'success.submissionCreated': 'Submission created successfully.',
  'success.profileSaved': 'Profile saved successfully.',

  // Relative time
  'time.justNow': 'Just now',
  'time.minutesAgo': '{count}m ago',
  'time.hoursAgo': '{count}h ago',
  'time.daysAgo': '{count}d ago',

  // Settings
  'settings.profile': 'Profile',
  'settings.organization': 'Organization',
  'settings.apiKeys': 'API Keys',
  'settings.notifications': 'Notifications',
  'settings.orgId': 'Org ID',
  'settings.tier': 'Tier',
  'settings.apiKeysDescription': 'Contact your organization administrator to manage API keys and integration credentials.',
  'settings.apiKeysNote': 'API keys provide programmatic access to Impact Core data. For security, key creation and revocation is restricted to organization admins.',
  'settings.notifReportAlerts': 'Report generation alerts',
  'settings.notifReportAlertsDesc': 'Email when a report is generated or fails',
  'settings.notifComplianceReminders': 'Compliance deadline reminders',
  'settings.notifComplianceRemindersDesc': 'Email reminders for upcoming filing deadlines',
  'settings.notifEsgUpdates': 'ESG submission updates',
  'settings.notifEsgUpdatesDesc': 'Email when investee submissions change status',
  'settings.notifInAppActivity': 'In-app activity feed',
  'settings.notifInAppActivityDesc': 'Show real-time activity notifications in header',

  // Data Completeness label
  'compliance.dataCompletenessLabel': 'Data Completeness',
};
